import { useCreateFavoriteOrderMutation, useCreateEpiFavoriteOrderMutation } from '@services';
import React, { createContext, ReactNode, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { GenericCartContextType, GenericCartItem } from './genericCart.types';

const GenericCartContext = createContext<GenericCartContextType | undefined>(undefined);

type GenericCartProviderProps = {
    children: ReactNode;
};

export type SaveCartStatus = 'success' | 'error';

export const GenericCartProvider: React.FC<GenericCartProviderProps> = ({ children }) => {
    const [items, setItems] = useState<GenericCartItem[]>([]);
    const [type, setType] = useState<'appro' | 'epi'>('appro');
    const previousTypeRef = useRef<'appro' | 'epi'>('appro');

    const [savedOrderId, setSavedOrderId] = useState<number | null>(null);

    const [createFavoriteOrder] = useCreateFavoriteOrderMutation();
    const [createEpiFavoriteOrder] = useCreateEpiFavoriteOrderMutation();

    const addItem = useCallback((product: GenericCartItem) => {
        setItems((currentItems) => {
            const existingItem = currentItems.find((item) => item.id === product.id);

            if (existingItem) {
                return currentItems.map((item) => (item.id === product.id ? { ...item, quantity: product.quantity } : item));
            }

            return [...currentItems, product];
        });
    }, []);

    const removeItem = useCallback((productId: string) => {
        setItems((currentItems) => currentItems.filter((item) => item.id !== productId));
    }, []);

    const updateQuantity = useCallback((productId: string, quantity: number) => {
        setItems((currentItems) => currentItems.map((item) => (item.id === productId ? { ...item, quantity } : item)));
    }, []);

    const clearCart = useCallback(() => {
        setItems([]);
        setSavedOrderId(null);
    }, []);

    const getItemQuantity = useCallback(
        (productId: string) => {
            return items.find((item) => item.id === productId)?.quantity || 0;
        },
        [items]
    );

    const getTotalItems = useCallback(() => {
        return items.reduce((total, item) => total + item.quantity, 0);
    }, [items]);

    const saveCart = useCallback(
        async (cartName: string, products: GenericCartItem[]): Promise<SaveCartStatus> => {
            const endpointWrapper = type === 'epi' ? createEpiFavoriteOrder : createFavoriteOrder;

            const response = await endpointWrapper({
                title: cartName,
                products: products.map((product) => ({
                    id: Number(product.id),
                    quantity: product.quantity
                }))
            });

            if (response.data?.success.code === 13001) {
                return 'success';
            } else {
                return 'error';
            }
        },
        [type, createEpiFavoriteOrder, createFavoriteOrder]
    );

    const setTypeCallback = useCallback((newType: React.SetStateAction<'appro' | 'epi'>) => {
        setType(newType);
    }, []);

    const setSavedOrderIdCallback = useCallback((id: number | null) => {
        setSavedOrderId(id);
    }, []);

    useEffect(() => {
        if (previousTypeRef.current !== type) {
            previousTypeRef.current = type;
            clearCart();
        }
    }, [type, clearCart]);

    const value = useMemo(
        () => ({
            items,
            type,
            addItem,
            removeItem,
            updateQuantity,
            clearCart,
            getItemQuantity,
            getTotalItems,
            saveCart,
            savedOrderId,
            setSavedOrderId: setSavedOrderIdCallback,
            setType: setTypeCallback
        }),
        [
            items,
            type,
            addItem,
            removeItem,
            updateQuantity,
            clearCart,
            getItemQuantity,
            getTotalItems,
            saveCart,
            savedOrderId,
            setSavedOrderIdCallback,
            setTypeCallback
        ]
    );

    return <GenericCartContext.Provider value={value}>{children}</GenericCartContext.Provider>;
};

export type UseGenericCartType = {
    type?: 'appro' | 'epi';
};

// eslint-disable-next-line react-refresh/only-export-components
export const useGenericCart = ({ type = 'appro' }: UseGenericCartType) => {
    const context = useContext(GenericCartContext);
    if (context === undefined) {
        throw new Error('useGenericCart must be used within a GenericCartProvider');
    }

    useEffect(() => {
        if (context.type !== type) {
            context.setType(type);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, context.type, context.setType]);

    return context;
};
